import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Library, Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;
const TYPES = [
  ['all', 'All types'], ['single_choice', 'Single choice'], ['multiple_choice', 'Multiple choice'],
  ['true_false', 'True / False'], ['fill_blank', 'Fill blank'], ['short_answer', 'Short answer'],
];

// Adds independent copies so editing in this quiz never changes other quizzes
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const toQuizQuestion = (q) => {
  // Normalise bank formats (correct answer as option text) to the quiz format (option ids + is_correct)
  const options = (q.options || []).map(o => ({
    ...o,
    id: o.id || newId(),
    is_correct: !!(o.is_correct || o.isCorrect || (q.correct_answer != null && o.text === q.correct_answer)),
  }));
  const correctCount = options.filter(o => o.is_correct).length;
  const type = q.type === 'multiple_choice' && correctCount <= 1 ? 'single_choice' : q.type;
  const usesOptions = options.length > 0 && ['single_choice', 'multiple_choice', 'true_false', 'image_based', 'scenario_based'].includes(type);
  return {
  id: newId(),
  type,
  question_text: q.question_text,
  options,
  correct_answer: usesOptions ? null : (q.correct_answer ?? null),
  ...(type === 'multiple_choice' && usesOptions ? { correct_answers: options.filter(o => o.is_correct).map(o => o.id) } : {}),
  explanation: q.explanation || '',
  points: q.points || 1,
  from_question_bank: true,
  question_bank_id: q.id,
  };
};

export const QuestionBankPicker = ({ existingBankIds = [], onAdd }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ questions: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => { const t = setTimeout(() => { setDebounced(search); setPage(0); }, 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (debounced) params.search = debounced;
      if (type !== 'all') params.type_filter = type;
      const res = await api.get('/builder/question-bank', { params });
      setData(res.data);
    } catch {
      setData({ questions: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [api, page, debounced, type]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (q) => setSelected(prev => { const n = { ...prev }; if (n[q.id]) delete n[q.id]; else n[q.id] = q; return n; });
  const count = Object.keys(selected).length;
  const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const handleAdd = () => {
    onAdd(Object.values(selected).map(toQuizQuestion));
    setSelected({});
    setOpen(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-[#20acf6] text-[#20acf6]" data-testid="pull-from-bank-btn">
        <Library className="h-4 w-4 mr-1" /> Pull from Bank
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="question-bank-modal">
          <DialogHeader>
            <DialogTitle>Question Bank</DialogTitle>
            <DialogDescription>Select questions to copy into this quiz. Editing a copy will not affect other quizzes.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions..." className="pl-9" data-testid="bank-search-input" />
            </div>
            <Select value={type} onValueChange={(v) => { setType(v); setPage(0); }}>
              <SelectTrigger className="w-44" data-testid="bank-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[240px]" data-testid="bank-question-list">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#20acf6]" /></div>
            ) : data.questions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-10">No questions found.</p>
            ) : data.questions.map(q => {
              const already = existingBankIds.includes(q.id);
              return (
                <label key={q.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${selected[q.id] ? 'border-[#20acf6] bg-blue-50' : 'border-slate-200 hover:bg-slate-50'} ${already ? 'opacity-60' : ''}`} data-testid={`bank-question-${q.id}`}>
                  <input type="checkbox" checked={!!selected[q.id]} onChange={() => toggle(q)} disabled={already} className="mt-1 accent-[#20acf6]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">{q.question_text}</p>
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{String(q.type).replace(/_/g, ' ')}</Badge>
                      {q.category && <Badge variant="outline" className="text-[10px]">{q.category}</Badge>}
                      {already && <Badge className="text-[10px] bg-slate-200 text-slate-600">Already in quiz</Badge>}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{data.total} questions</span>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="bank-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span>Page {page + 1} of {pages}</span>
              <Button size="icon" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)} data-testid="bank-next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={!count} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="bank-add-selected-btn">
              Add {count || ''} question{count === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
